import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'

import 'bootstrap/dist/css/bootstrap.min.css'
import './index.scss'

import {
    ApolloClient,
    InMemoryCache,
    ApolloProvider,
    HttpLink,
    DefaultOptions,
    ApolloLink,
    ServerError
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"

import { ErrorBoundary } from '@highlight-run/react';
import { MsalProvider } from '@azure/msal-react'
import useMsalInstance from './hooks/useMsalInstance'
import { AuthProvider, useAuth } from './context/AuthContext'
import { onError } from '@apollo/client/link/error'
import { toast } from 'react-toastify'
import { H } from 'highlight.run'
import { getAuthorizationToken } from "./utils/tokenUtils";
import { getTokenStatus } from "./utils/tokenUtils";
import { HighlightUserIdentifier } from './components/HighlightUserIdentifier';

// Only initialize Highlight.io in production - check if running on localhost
const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
if (!isLocalDevelopment) {
    H.init('3ejnp45g', {
        serviceName: "frontend-app",
        tracingOrigins: true,
        networkRecording: {
            enabled: true,
            recordHeadersAndBody: true,
            urlBlocklist: [],
        },
    });
}

// Add global debug utilities for token monitoring
if (typeof window !== 'undefined') {
    (window as any).checkTokenStatus = () => {
        const status = getTokenStatus();
        console.table(status);
        return status;
    };

    // Add user-friendly timeout message handler
    (window as any).showTokenTimeoutMessage = () => {
        toast.info(
            "Your session is taking longer than usual to refresh. The page will reload shortly to ensure you stay signed in.",
            {
                position: "top-center",
                autoClose: 3000,
                onClose: () => {
                    window.location.reload();
                }
            }
        );
    };
}


const httpLink = new HttpLink({
    uri: `${window.location.origin}/graphql`,
    credentials: 'same-origin'
})


const defaultOptions: DefaultOptions = {
    watchQuery: {
        fetchPolicy: "no-cache"
    },
    query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all"
    }
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path, extensions }) => {
            console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Extensions: ${JSON.stringify(extensions)}`);

            // Handle client-side cancellations gracefully (don't show error to user)
            if (extensions?.code === "REQUEST_CANCELLED" || message.includes("was cancelled") || message.includes("was canceled")) {
                console.info("Request was cancelled by client (user navigated away)", { operation: operation.operationName });
                return; // Don't show error toast for cancellations
            }

            // Handle authentication errors specifically
            if (extensions?.code === "AUTH_NOT_AUTHENTICATED") {
                console.warn("Authentication error detected, clearing token cache and attempting refresh");
                // Clear token cache and attempt to refresh
                import("./utils/tokenUtils").then(({ clearTokenCache }) => {
                    clearTokenCache();
                });
                toast.error("Your session has expired. Please refresh the page to continue.");
            } else if (extensions?.message === "Attempted to perform an unauthorized operation.") {
                toast.error("Access Denied: You do not have permission to perform this operation.");
            } else {
                toast.error(`${extensions?.message ?? message}`);
            }
        });

        // If we have GraphQL errors, don't also show network error toasts
        // The GraphQL errors are more user-friendly and specific
        return;
    }

    if (networkError) {
        console.error("Network error:", networkError);

        // Handle client-side request aborts (user navigating away)
        if (networkError.name === 'AbortError' || networkError.message.includes('aborted')) {
            console.info("Request was aborted by client", { operation: operation.operationName });
            return; // Don't show error toast for client aborts
        }

        // Handle network errors that might indicate auth issues
        // ServerError includes statusCode from HTTP responses
        const isServerError = (error: any): error is ServerError => {
            return error && typeof error.statusCode === 'number';
        };

        if (isServerError(networkError) && networkError.statusCode === 401) {
            console.warn("401 Unauthorized response, clearing token cache");
            import("./utils/tokenUtils").then(({ clearTokenCache }) => {
                clearTokenCache();
            });
            toast.error("Authentication error: Please refresh the page to continue.");
        } else {
            toast.error(`Network error: ${networkError.message}`);
        }
    }
});

const MsalWrapper = () => {
    const { msalInstance, isInitialized, authority } = useMsalInstance();

    if (!isInitialized) {
        return <div>Loading...</div>;
    }

    return (

        <ErrorBoundary>
            <MsalProvider instance={msalInstance}>
                <AuthProvider msalInstance={msalInstance} authority={authority}>
                    <ApolloWrapper />
                </AuthProvider>
            </MsalProvider>
        </ErrorBoundary>
    );
};


const ApolloWrapper = () => {
    const { account, msalInstance, isLoading, authority, logout } = useAuth()

    if (isLoading) {
        return <div>Loading...</div>;
    }


    const authLink = setContext(async (_, { headers }) => {
        const token = await getAuthorizationToken(msalInstance, account, authority, logout);

        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : "",
            },
        };
    });


    const client = new ApolloClient({
        link: ApolloLink.from([
            errorLink,
            authLink,
            httpLink,
        ]),
        cache: new InMemoryCache(),
        defaultOptions
    });

    return (
        <ApolloProvider client={client}>
            <App />
        </ApolloProvider>
    )
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(<MsalWrapper />);